import { useState } from "react";
import { toast } from "sonner";
import { siteConfig } from "../config/siteConfig";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";

const initial = {
  name: "",
  email: "",
  phone: "",
  checkIn: "",
  checkOut: "",
  guests: "2",
  roomType: "Deluxe Room",
  message: "",
};

export default function Contact() {
  const { contact, bookingLinks, bookingChannels, features } = siteConfig;
  const [form, setForm] = useState(initial);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildText = () => {
    return (
      `Hello THURULIYA HERITAGE,\n\n` +
      `I would like to inquire about a booking:\n` +
      `Name: ${form.name}\n` +
      `Email: ${form.email}\n` +
      `Phone: ${form.phone}\n` +
      `Check-in: ${form.checkIn}\n` +
      `Check-out: ${form.checkOut}\n` +
      `Guests: ${form.guests}\n` +
      `Room type: ${form.roomType}\n\n` +
      `Message: ${form.message}`
    );
  };

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    const text = encodeURIComponent(buildText());
    window.open(`https://wa.me/${contact.whatsapp}?text=${text}`, "_blank");
    toast.success("Opening WhatsApp with your inquiry...");
  };

  const handleEmailSubmit = () => {
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    const subject = encodeURIComponent("Booking Inquiry — THURULIYA HERITAGE");
    const body = encodeURIComponent(buildText());
    window.location.href = `mailto:${contact.email}?subject=${subject}&body=${body}`;
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section bg-[#FAF9F6]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 reveal">
          <p className="font-overline mb-4">Reach Us</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1A362D] mb-4">
            Contact & Booking Inquiry
          </h2>
          <div className="gold-divider"></div>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-12">
          {/* Left: Contact info + map */}
          <aside className="lg:col-span-2 reveal">
            <h3 className="font-display text-2xl sm:text-3xl text-[#1A362D] mb-6">
              Get in Touch
            </h3>
            <ul className="space-y-5 mb-8">
              <li className="flex gap-4">
                <MapPin
                  className="w-5 h-5 text-[#C5A059] mt-1 shrink-0"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-[#5C4033] mb-1">
                    Address
                  </p>
                  <p className="text-[#2C2C2C]">{contact.address}</p>
                </div>
              </li>
              <li className="flex gap-4">
                <Phone
                  className="w-5 h-5 text-[#C5A059] mt-1 shrink-0"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-[#5C4033] mb-1">
                    Phone
                  </p>
                  <a
                    href={`tel:${contact.phone}`}
                    className="text-[#2C2C2C] hover:text-[#C5A059]"
                    data-testid="contact-phone-link"
                  >
                    {contact.phone}
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <Mail
                  className="w-5 h-5 text-[#C5A059] mt-1 shrink-0"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-[#5C4033] mb-1">
                    Email
                  </p>
                  <a
                    href={`mailto:${contact.email}`}
                    className="text-[#2C2C2C] hover:text-[#C5A059]"
                    data-testid="contact-email-link"
                  >
                    {contact.email}
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <MessageCircle
                  className="w-5 h-5 text-[#C5A059] mt-1 shrink-0"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-[#5C4033] mb-1">
                    WhatsApp
                  </p>
                  <a
                    href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(
                      siteConfig.whatsappDefaultMessage
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#2C2C2C] hover:text-[#C5A059]"
                    data-testid="contact-whatsapp-link"
                  >
                    +{contact.whatsapp}
                  </a>
                </div>
              </li>
            </ul>

            <div className="flex flex-wrap gap-2 mb-8">
              {bookingChannels.showWhatsApp && (
                <a
                  href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(
                    siteConfig.whatsappDefaultMessage
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary !py-2.5 !px-4 !text-[10px]"
                  data-testid="quick-whatsapp-btn"
                >
                  WhatsApp Inquiry
                </a>
              )}
              {bookingChannels.showBookingCom && (
                <a
                  href={bookingLinks.bookingCom}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline-dark !py-2.5 !px-4 !text-[10px]"
                  data-testid="quick-bookingcom-btn"
                >
                  Booking.com
                </a>
              )}
              {bookingChannels.showAgoda && (
                <a
                  href={bookingLinks.agoda}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline-dark !py-2.5 !px-4 !text-[10px]"
                  data-testid="quick-agoda-btn"
                >
                  Agoda
                </a>
              )}
            </div>

            <div className="aspect-video w-full rounded-3xl overflow-hidden">
              <iframe
                src={contact.mapEmbedUrl}
                title="Map"
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>

          {/* Right: Inquiry form */}
          {features.showInquiryForm && (
            <form
              onSubmit={handleWhatsAppSubmit}
              className="lg:col-span-3 reveal bg-[#F3EFE6] rounded-3xl p-6 sm:p-10"
              data-testid="inquiry-form"
            >
              <h3 className="font-display text-2xl sm:text-3xl text-[#1A362D] mb-6">
                Send an Inquiry
              </h3>

              <div className="grid sm:grid-cols-2 gap-4">
                <FormInput
                  label="Name"
                  value={form.name}
                  onChange={update("name")}
                  testid="form-name"
                />
                <FormInput
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  testid="form-email"
                />
                <FormInput
                  label="Phone"
                  value={form.phone}
                  onChange={update("phone")}
                  testid="form-phone"
                />
                <FormSelect
                  label="Room Type"
                  value={form.roomType}
                  onChange={update("roomType")}
                  options={siteConfig.rooms.map((r) => r.name)}
                  testid="form-room-type"
                />
                <FormInput
                  label="Check-in"
                  type="date"
                  value={form.checkIn}
                  onChange={update("checkIn")}
                  testid="form-checkin"
                />
                <FormInput
                  label="Check-out"
                  type="date"
                  value={form.checkOut}
                  onChange={update("checkOut")}
                  testid="form-checkout"
                />
                <FormSelect
                  label="Guests"
                  value={form.guests}
                  onChange={update("guests")}
                  options={["1", "2", "3", "4", "5", "6+"]}
                  testid="form-guests"
                />
              </div>

              <div className="mt-4">
                <label className="block text-xs uppercase tracking-[0.22em] text-[#5C4033] mb-2">
                  Message
                </label>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  className="w-full bg-[#FAF9F6] rounded-2xl border border-[#EBE5D9] focus:border-[#C5A059] outline-none p-4 text-[#2C2C2C] text-sm transition-colors"
                  data-testid="form-message"
                />
              </div>

              <div className="flex flex-wrap gap-3 mt-8">
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="form-submit-whatsapp"
                >
                  Send via WhatsApp
                </button>
                <button
                  type="button"
                  onClick={handleEmailSubmit}
                  className="btn-outline-dark"
                  data-testid="form-submit-email"
                >
                  Send via Email
                </button>
              </div>
              <p className="text-xs text-[#5C4033]/80 mt-4">
                Submissions open your WhatsApp or email client with your inquiry
                pre-filled.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function FormInput({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.22em] text-[#5C4033] mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-[#FAF9F6] rounded-xl border border-[#EBE5D9] focus:border-[#C5A059] outline-none p-3.5 text-[#2C2C2C] text-sm transition-colors"
      />
    </div>
  );
}

function FormSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.22em] text-[#5C4033] mb-2">
        {label}
      </label>
      <select
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-[#FAF9F6] rounded-xl border border-[#EBE5D9] focus:border-[#C5A059] outline-none p-3.5 text-[#2C2C2C] text-sm transition-colors"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
